import React, { useEffect, useState } from 'react';
import { hrAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Plus, Loader2, Upload, Download, Trash2, FileText, Calendar as CalIcon,
  CheckCircle2, XCircle, Clock, AlertTriangle, Star, GraduationCap, Shield, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const LEAVE_BADGES = {
  approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  rejected: 'bg-rose-100 text-rose-700 border-rose-200',
  recorded: 'bg-slate-100 text-slate-700 border-slate-200',
};

const LEAVE_TYPES = [
  { value: 'holiday', label: 'Holiday' },
  { value: 'sickness', label: 'Sickness' },
  { value: 'absence', label: 'Absence' },
  { value: 'parental', label: 'Parental' },
  { value: 'other', label: 'Other' },
];

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const bytes = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// ─── Documents tab ─────────────────────────────────────────────

export const HrDocumentsTab = ({ employeeId, canWrite, isSelf, isHrAdmin }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [filterCat, setFilterCat] = useState('all');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadDraft, setUploadDraft] = useState({ category: 'other', notes: '', file: null });
  const [uploading, setUploading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await hrAPI.listDocuments(employeeId, filterCat === 'all' ? null : filterCat);
      setDocs(r.data || []);
    } catch (e) { toast.error('Could not load documents'); } finally { setLoading(false); }
  };

  useEffect(() => {
    hrAPI.documentCategories().then(r => setCategories(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [employeeId, filterCat]);

  const handleUpload = async () => {
    if (!uploadDraft.file) { toast.error('Select a file'); return; }
    setUploading(true);
    try {
      await hrAPI.uploadDocument(employeeId, uploadDraft.file, uploadDraft.category, uploadDraft.notes);
      toast.success('Document uploaded to SharePoint');
      setUploadOpen(false);
      setUploadDraft({ category: 'other', notes: '', file: null });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const handleDownload = async (doc) => {
    try {
      const r = await hrAPI.downloadDocument(doc.id);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = doc.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"? This removes it from SharePoint too.`)) return;
    try {
      await hrAPI.deleteDocument(doc.id);
      toast.success('Deleted');
      refresh();
    } catch (e) { toast.error('Delete failed'); }
  };

  // Allowed upload categories: HR Admin = all; self = personal_details only
  const allowedUploadCats = isHrAdmin ? categories : categories.filter(c => c.value === 'personal_details');

  return (
    <Card className="border-slate-200" data-testid="hr-documents-tab">
      <CardHeader className="pb-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-500" /> Documents
        </CardTitle>
        <div className="flex items-center gap-2">
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-[180px]" data-testid="doc-filter-cat">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {canWrite && allowedUploadCats.length > 0 && (
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setUploadOpen(true)} data-testid="hr-doc-upload-btn">
              <Upload className="w-4 h-4 mr-1" /> Upload
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-start gap-2 p-2.5 rounded bg-blue-50 border border-blue-200 text-xs text-blue-900">
          <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Files are stored encrypted in SharePoint under <strong>Human Resources</strong>. Access is restricted to the HR Owner.</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : docs.length === 0 ? (
          <p className="text-center text-slate-400 py-10 text-sm">No documents in this category yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {docs.map(d => (
                <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                  <TableCell>
                    <p className="font-medium text-sm">{d.filename}</p>
                    <p className="text-[11px] text-slate-500">{bytes(d.size)}{d.notes ? ` · ${d.notes}` : ''}</p>
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-slate-100 text-slate-700 border-slate-200 border text-[10px]">
                      {categories.find(c => c.value === d.category)?.label || d.category}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {fmt(d.uploaded_at)}<br />
                    <span className="text-[10px]">by {d.uploaded_by}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      {d.sharepoint_web_url && (
                        <Button size="sm" variant="ghost" asChild data-testid={`doc-open-${d.id}`}>
                          <a href={d.sharepoint_web_url} target="_blank" rel="noreferrer"><ExternalLink className="w-3.5 h-3.5" /></a>
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => handleDownload(d)} data-testid={`doc-dl-${d.id}`}>
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      {isHrAdmin && (
                        <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => handleDelete(d)} data-testid={`doc-del-${d.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent data-testid="hr-doc-upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload HR Document</DialogTitle>
            <DialogDescription>Stored under Human Resources / Employee Folder / Category.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={uploadDraft.category} onValueChange={(v) => setUploadDraft({ ...uploadDraft, category: v })}>
                <SelectTrigger data-testid="doc-up-cat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {allowedUploadCats.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">File</Label>
              <Input type="file" onChange={(e) => setUploadDraft({ ...uploadDraft, file: e.target.files?.[0] || null })} data-testid="doc-up-file" />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={uploadDraft.notes} onChange={(e) => setUploadDraft({ ...uploadDraft, notes: e.target.value })} placeholder="Optional context" data-testid="doc-up-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="doc-up-save">
              {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ─── Leave tab ─────────────────────────────────────────────────

export const HrLeaveTab = ({ employeeId, canApprove, isSelf, isHrAdmin }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ type: 'holiday', start_date: '', end_date: '', reason: '' });
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await hrAPI.listLeave(employeeId);
      setRows(r.data || []);
    } catch (e) { toast.error('Could not load leave'); } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [employeeId]);

  const create = async () => {
    if (!draft.start_date) { toast.error('Start date required'); return; }
    setSaving(true);
    try {
      await hrAPI.createLeave(employeeId, draft);
      toast.success(isHrAdmin ? 'Leave recorded' : 'Leave request submitted');
      setOpen(false);
      setDraft({ type: 'holiday', start_date: '', end_date: '', reason: '' });
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create'); } finally { setSaving(false); }
  };

  const setStatus = async (id, status) => {
    try { await hrAPI.updateLeave(id, { status }); toast.success(`Marked ${status}`); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this leave record?')) return;
    try { await hrAPI.deleteLeave(id); toast.success('Deleted'); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <Card className="border-slate-200" data-testid="hr-leave-tab">
      <CardHeader className="pb-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <CardTitle className="text-base flex items-center gap-2"><CalIcon className="w-4 h-4 text-slate-500" /> Leave & Absences</CardTitle>
        <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setOpen(true)} data-testid="leave-new-btn">
          <Plus className="w-4 h-4 mr-1" /> {isSelf && !isHrAdmin ? 'Request Leave' : 'Record Leave'}
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
         : rows.length === 0 ? <p className="text-center text-slate-400 py-10 text-sm">No leave records.</p>
         : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Type</TableHead><TableHead>Dates</TableHead><TableHead>Days</TableHead>
              <TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} data-testid={`leave-row-${r.id}`}>
                  <TableCell className="capitalize text-sm">{r.type}</TableCell>
                  <TableCell className="text-sm">{fmt(r.start_date)} → {fmt(r.end_date)}</TableCell>
                  <TableCell className="text-sm">{r.days || 0}</TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-[200px] truncate">{r.reason || '—'}</TableCell>
                  <TableCell>
                    <Badge className={`${LEAVE_BADGES[r.status] || LEAVE_BADGES.recorded} border text-[10px]`}>{r.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      {canApprove && r.status === 'pending' && (
                        <>
                          <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => setStatus(r.id, 'approved')} data-testid={`leave-approve-${r.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setStatus(r.id, 'rejected')} data-testid={`leave-reject-${r.id}`}>
                            <XCircle className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                      {(isHrAdmin || (isSelf && r.status === 'pending')) && (
                        <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => remove(r.id)} data-testid={`leave-del-${r.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="leave-create-dialog">
          <DialogHeader>
            <DialogTitle>{isSelf && !isHrAdmin ? 'Request Leave' : 'Record Leave'}</DialogTitle>
            <DialogDescription>{isSelf && !isHrAdmin ? 'Your request will be sent for approval.' : 'Record this directly as approved or pending.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
                <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Start</Label><Input type="date" value={draft.start_date} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} data-testid="leave-start" /></div>
              <div><Label className="text-xs">End</Label><Input type="date" value={draft.end_date} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} data-testid="leave-end" /></div>
            </div>
            <div>
              <Label className="text-xs">Reason / Notes</Label>
              <Textarea rows={3} value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} data-testid="leave-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={create} disabled={saving} data-testid="leave-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ─── Performance tab ───────────────────────────────────────────

export const HrPerformanceTab = ({ employeeId, canWrite, isHrAdmin }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ period_label: '', review_date: '', rating: '', strengths: '', improvements: '', goals: '', comments: '' });
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try { const r = await hrAPI.listReviews(employeeId); setRows(r.data || []); }
    catch (e) { toast.error('Could not load reviews'); } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [employeeId]);

  const create = async () => {
    setSaving(true);
    try { await hrAPI.createReview(employeeId, draft); toast.success('Review saved'); setOpen(false); setDraft({ period_label: '', review_date: '', rating: '', strengths: '', improvements: '', goals: '', comments: '' }); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try { await hrAPI.deleteReview(id); toast.success('Deleted'); refresh(); } catch { toast.error('Delete failed'); }
  };

  return (
    <Card className="border-slate-200" data-testid="hr-performance-tab">
      <CardHeader className="pb-3 flex items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2"><Star className="w-4 h-4 text-slate-500" /> Performance Reviews</CardTitle>
        {canWrite && <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setOpen(true)} data-testid="review-new-btn"><Plus className="w-4 h-4 mr-1" /> New Review</Button>}
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
         : rows.length === 0 ? <p className="text-center text-slate-400 py-10 text-sm">No reviews recorded yet.</p>
         : rows.map(r => (
          <div key={r.id} className="p-3 rounded border border-slate-200 bg-white" data-testid={`review-row-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="text-sm font-semibold">{r.period_label || 'Review'} <span className="text-xs text-slate-400 ml-2">{fmt(r.review_date)}</span></p>
                <p className="text-[11px] text-slate-500">By {r.reviewer_name}{r.rating ? ` · Rating: ${r.rating}` : ''}</p>
                {r.strengths && <p className="text-xs mt-2"><strong>Strengths:</strong> {r.strengths}</p>}
                {r.improvements && <p className="text-xs mt-1"><strong>Improvements:</strong> {r.improvements}</p>}
                {r.goals && <p className="text-xs mt-1"><strong>Goals:</strong> {r.goals}</p>}
                {r.comments && <p className="text-xs mt-1 text-slate-600">{r.comments}</p>}
              </div>
              {isHrAdmin && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(r.id)} data-testid={`review-del-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
            </div>
          </div>
        ))}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="review-dialog">
          <DialogHeader><DialogTitle>New Performance Review</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Period</Label><Input placeholder="e.g. H2 2025" value={draft.period_label} onChange={(e) => setDraft({ ...draft, period_label: e.target.value })} data-testid="r-period" /></div>
              <div><Label className="text-xs">Review Date</Label><Input type="date" value={draft.review_date} onChange={(e) => setDraft({ ...draft, review_date: e.target.value })} data-testid="r-date" /></div>
            </div>
            <div><Label className="text-xs">Rating</Label><Input placeholder="e.g. 4/5 or Exceeds" value={draft.rating} onChange={(e) => setDraft({ ...draft, rating: e.target.value })} data-testid="r-rating" /></div>
            <div><Label className="text-xs">Strengths</Label><Textarea rows={2} value={draft.strengths} onChange={(e) => setDraft({ ...draft, strengths: e.target.value })} data-testid="r-strengths" /></div>
            <div><Label className="text-xs">Improvements</Label><Textarea rows={2} value={draft.improvements} onChange={(e) => setDraft({ ...draft, improvements: e.target.value })} data-testid="r-improvements" /></div>
            <div><Label className="text-xs">Goals</Label><Textarea rows={2} value={draft.goals} onChange={(e) => setDraft({ ...draft, goals: e.target.value })} data-testid="r-goals" /></div>
            <div><Label className="text-xs">Comments</Label><Textarea rows={2} value={draft.comments} onChange={(e) => setDraft({ ...draft, comments: e.target.value })} data-testid="r-comments" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={create} disabled={saving} data-testid="r-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Save Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ─── Training tab ──────────────────────────────────────────────

export const HrTrainingTab = ({ employeeId, isHrAdmin }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ course_name: '', provider: '', completed_at: '', expiry_date: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try { const r = await hrAPI.listTraining(employeeId); setRows(r.data || []); }
    catch (e) { toast.error('Could not load training'); } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [employeeId]);

  const create = async () => {
    if (!draft.course_name.trim()) { toast.error('Course name required'); return; }
    setSaving(true);
    try { await hrAPI.createTraining(employeeId, draft); toast.success('Training recorded'); setOpen(false); setDraft({ course_name: '', provider: '', completed_at: '', expiry_date: '', notes: '' }); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this training record?')) return;
    try { await hrAPI.deleteTraining(id); toast.success('Deleted'); refresh(); } catch { toast.error('Delete failed'); }
  };

  const isExpired = (d) => d && new Date(d) < new Date();

  return (
    <Card className="border-slate-200" data-testid="hr-training-tab">
      <CardHeader className="pb-3 flex items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2"><GraduationCap className="w-4 h-4 text-slate-500" /> Training Records</CardTitle>
        {isHrAdmin && <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setOpen(true)} data-testid="training-new-btn"><Plus className="w-4 h-4 mr-1" /> Add Training</Button>}
      </CardHeader>
      <CardContent>
        {loading ? <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
         : rows.length === 0 ? <p className="text-center text-slate-400 py-10 text-sm">No training records yet.</p>
         : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Course</TableHead><TableHead>Provider</TableHead><TableHead>Completed</TableHead><TableHead>Expires</TableHead>
              {isHrAdmin && <TableHead className="text-right">Actions</TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} data-testid={`training-row-${r.id}`}>
                  <TableCell>
                    <p className="text-sm font-medium">{r.course_name}</p>
                    {r.notes && <p className="text-[11px] text-slate-500">{r.notes}</p>}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{r.provider || '—'}</TableCell>
                  <TableCell className="text-sm">{fmt(r.completed_at)}</TableCell>
                  <TableCell className="text-sm">
                    {r.expiry_date ? (
                      <span className={isExpired(r.expiry_date) ? 'text-rose-600 font-medium' : ''}>
                        {fmt(r.expiry_date)} {isExpired(r.expiry_date) && <AlertTriangle className="inline w-3 h-3 ml-1" />}
                      </span>
                    ) : '—'}
                  </TableCell>
                  {isHrAdmin && (
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(r.id)} data-testid={`training-del-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="training-dialog">
          <DialogHeader><DialogTitle>Add Training Record</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Course Name *</Label><Input value={draft.course_name} onChange={(e) => setDraft({ ...draft, course_name: e.target.value })} data-testid="t-course" /></div>
            <div><Label className="text-xs">Provider</Label><Input value={draft.provider} onChange={(e) => setDraft({ ...draft, provider: e.target.value })} data-testid="t-provider" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Completed</Label><Input type="date" value={draft.completed_at} onChange={(e) => setDraft({ ...draft, completed_at: e.target.value })} data-testid="t-completed" /></div>
              <div><Label className="text-xs">Expires</Label><Input type="date" value={draft.expiry_date} onChange={(e) => setDraft({ ...draft, expiry_date: e.target.value })} data-testid="t-expires" /></div>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} data-testid="t-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={create} disabled={saving} data-testid="t-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ─── Disciplinaries tab (HR Admin only) ────────────────────────

export const HrDisciplinariesTab = ({ employeeId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ type: 'verbal', date: '', summary: '', outcome: '' });
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try { const r = await hrAPI.listDisciplinaries(employeeId); setRows(r.data || []); }
    catch (e) { toast.error('No access or load failed'); } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [employeeId]);

  const create = async () => {
    setSaving(true);
    try { await hrAPI.createDisciplinary(employeeId, draft); toast.success('Recorded'); setOpen(false); setDraft({ type: 'verbal', date: '', summary: '', outcome: '' }); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this disciplinary record?')) return;
    try { await hrAPI.deleteDisciplinary(id); toast.success('Deleted'); refresh(); } catch { toast.error('Delete failed'); }
  };

  return (
    <Card className="border-amber-200 bg-amber-50/30" data-testid="hr-disc-tab">
      <CardHeader className="pb-3 flex items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> Disciplinary Notes <Shield className="w-3 h-3 text-amber-500 ml-1" /></CardTitle>
        <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setOpen(true)} data-testid="disc-new-btn"><Plus className="w-4 h-4 mr-1" /> New Record</Button>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-amber-800 mb-2">Visible only to HR Admins. Records may be uploaded as PDF via Documents → Disciplinaries.</p>
        {loading ? <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
         : rows.length === 0 ? <p className="text-center text-slate-400 py-10 text-sm">No disciplinary records.</p>
         : rows.map(r => (
          <div key={r.id} className="p-3 rounded border border-amber-200 bg-white" data-testid={`disc-row-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="text-sm font-semibold capitalize">{r.type} <span className="text-xs text-slate-400 ml-2">{fmt(r.date)}</span></p>
                <p className="text-[11px] text-slate-500">Recorded by {r.recorded_by}</p>
                {r.summary && <p className="text-xs mt-2"><strong>Summary:</strong> {r.summary}</p>}
                {r.outcome && <p className="text-xs mt-1"><strong>Outcome:</strong> {r.outcome}</p>}
              </div>
              <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(r.id)} data-testid={`disc-del-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="disc-dialog">
          <DialogHeader><DialogTitle>New Disciplinary Record</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
                <SelectTrigger data-testid="d-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="verbal">Verbal Warning</SelectItem>
                  <SelectItem value="written">Written Warning</SelectItem>
                  <SelectItem value="final_written">Final Written Warning</SelectItem>
                  <SelectItem value="dismissal">Dismissal</SelectItem>
                  <SelectItem value="grievance">Grievance</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Date</Label><Input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} data-testid="d-date" /></div>
            <div><Label className="text-xs">Summary</Label><Textarea rows={3} value={draft.summary} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} data-testid="d-summary" /></div>
            <div><Label className="text-xs">Outcome</Label><Textarea rows={2} value={draft.outcome} onChange={(e) => setDraft({ ...draft, outcome: e.target.value })} data-testid="d-outcome" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={create} disabled={saving} data-testid="d-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
